import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/helpers';

export const CreateProject = () => {
  const navigate = useNavigate();
  const { addProject } = useProjects();
  const { user } = useAuth();
  
  // Default payment types
  const defaultPaymentTypes = [
    { value: 'outsourced', label: 'Outsourced Project (Milestone + Advance)' },
    { value: 'recurring', label: 'Recurring Payment' },
    { value: 'one-time', label: 'One-time Payment (Split)' },
  ];
  
  // Custom payment types from localStorage
  const [customPaymentTypes, setCustomPaymentTypes] = useState([]);
  const [showAddPaymentType, setShowAddPaymentType] = useState(false);
  const [newPaymentType, setNewPaymentType] = useState('');
  
  // Load custom payment types on mount
  useEffect(() => {
    const stored = localStorage.getItem('customPaymentTypes');
    if (stored) {
      setCustomPaymentTypes(JSON.parse(stored));
    }
  }, []);
  
  // All available payment types
  const allPaymentTypes = [...defaultPaymentTypes, ...customPaymentTypes];
  
  const [formData, setFormData] = useState({
    projectName: '',
    clientName: '',
    invoiceNumber: '',
    paymentType: 'outsourced',
  });

  // Outsourced project fields
  const [outsourcedData, setOutsourcedData] = useState({
    totalValue: '',
    advanceAmount: '',
    outsourcingPartner: '',
    advanceInvoiceNumber: '',
    milestones: [
      { paymentName: '', amount: '', dueDate: '', invoiceNumber: '' },
    ],
  });

  // Recurring project fields
  const [recurringData, setRecurringData] = useState({
    totalValue: '',
    startDate: '',
    recurrence: 'monthly',
    planAmount: '',
    paymentInvoices: [],
  });

  // One-time project fields
  const [oneTimeData, setOneTimeData] = useState({
    totalAmount: '',
    advanceAmount: '',
    remainingAmount: '',
    dueDate: '',
    advanceInvoiceNumber: '',
    finalInvoiceNumber: '',
  });

  // Custom payment type fields
  const [customData, setCustomData] = useState({
    amount: '',
    dueDate: '',
    paymentName: '',
    invoiceNumber: '',
  });

  const handleAddMilestone = () => {
    setOutsourcedData({
      ...outsourcedData,
      milestones: [...outsourcedData.milestones, { paymentName: '', amount: '', dueDate: '', invoiceNumber: '' }],
    });
  };

  const handleRemoveMilestone = (index) => {
    const newMilestones = outsourcedData.milestones.filter((_, i) => i !== index);
    setOutsourcedData({ ...outsourcedData, milestones: newMilestones });
  };

  const handleRecurringInvoiceChange = (index, value) => {
    const paymentInvoices = [...(recurringData.paymentInvoices || [])];
    paymentInvoices[index] = value;
    setRecurringData({ ...recurringData, paymentInvoices });
  };

  // Compute recurring payments for the live preview (mirrors generatePaymentSchedule logic)
  const computeRecurringPayments = () => {
    if (!recurringData.startDate || !recurringData.planAmount) return [];
    const startDate = new Date(recurringData.startDate);
    const planAmount = parseFloat(recurringData.planAmount);
    const recurrence = recurringData.recurrence;

    let monthsToGenerate = 12;
    if (recurrence === 'quarterly') monthsToGenerate = 4;
    if (recurrence === 'half-yearly') monthsToGenerate = 2;
    if (recurrence === 'yearly') monthsToGenerate = 1;

    const monthIncrement = recurrence === 'monthly' ? 1 : recurrence === 'quarterly' ? 3 : recurrence === 'half-yearly' ? 6 : 12;

    const payments = [];
    for (let i = 0; i < monthsToGenerate; i++) {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + (i * monthIncrement));
      payments.push({
        paymentName: `Payment ${i + 1} (${paymentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})`,
        amount: planAmount,
        dueDate: paymentDate.toISOString().split('T')[0],
      });
    }
    return payments;
  };

  const handleAddPaymentType = () => {
    if (!newPaymentType.trim()) {
      toast.error('Please enter a payment type name');
      return;
    }
    
    const value = newPaymentType.toLowerCase().replace(/\s+/g, '-');
    const newType = { value, label: newPaymentType };
    
    const updated = [...customPaymentTypes, newType];
    setCustomPaymentTypes(updated);
    localStorage.setItem('customPaymentTypes', JSON.stringify(updated));
    
    setFormData({ ...formData, paymentType: value });
    setNewPaymentType('');
    setShowAddPaymentType(false);
    toast.success('Payment type added successfully');
  };

  const handleMilestoneChange = (index, field, value) => {
    const newMilestones = [...outsourcedData.milestones];
    newMilestones[index][field] = value;
    setOutsourcedData({ ...outsourcedData, milestones: newMilestones });
  };

  const generatePaymentSchedule = () => {
    const schedule = [];

    if (formData.paymentType === 'outsourced') {
      // Add advance payment
      if (outsourcedData.advanceAmount) {
        schedule.push({
          id: `payment-${Date.now()}-advance`,
          paymentName: 'Advance Payment',
          amount: parseFloat(outsourcedData.advanceAmount),
          dueDate: new Date().toISOString().split('T')[0],
          invoiceNumber: outsourcedData.advanceInvoiceNumber || '',
          status: 'pending',
          notes: '',
        });
      }
      // Add milestones
      outsourcedData.milestones.forEach((milestone, idx) => {
        if (milestone.paymentName && milestone.amount && milestone.dueDate) {
          schedule.push({
            id: `payment-${Date.now()}-${idx}`,
            paymentName: milestone.paymentName,
            amount: parseFloat(milestone.amount),
            dueDate: milestone.dueDate,
            invoiceNumber: milestone.invoiceNumber || '',
            status: 'pending',
            notes: '',
          });
        }
      });
    } else if (formData.paymentType === 'recurring') {
      // Generate recurring payments for 12 months
      const startDate = new Date(recurringData.startDate);
      const planAmount = parseFloat(recurringData.planAmount);
      const recurrence = recurringData.recurrence;

      let monthsToGenerate = 12;
      if (recurrence === 'quarterly') monthsToGenerate = 4;
      if (recurrence === 'half-yearly') monthsToGenerate = 2;
      if (recurrence === 'yearly') monthsToGenerate = 1;

      const monthIncrement = recurrence === 'monthly' ? 1 : recurrence === 'quarterly' ? 3 : recurrence === 'half-yearly' ? 6 : 12;

      for (let i = 0; i < monthsToGenerate; i++) {
        const paymentDate = new Date(startDate);
        paymentDate.setMonth(paymentDate.getMonth() + (i * monthIncrement));
        
        schedule.push({
          id: `payment-${Date.now()}-${i}`,
          paymentName: `Payment ${i + 1} (${paymentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})`,
          amount: planAmount,
          dueDate: paymentDate.toISOString().split('T')[0],
          invoiceNumber: (recurringData.paymentInvoices || [])[i] || '',
          status: 'pending',
          notes: '',
        });
      }
    } else if (formData.paymentType === 'one-time') {
      // Add advance payment
      if (oneTimeData.advanceAmount) {
        schedule.push({
          id: `payment-${Date.now()}-advance`,
          paymentName: 'Advance Payment',
          amount: parseFloat(oneTimeData.advanceAmount),
          dueDate: new Date().toISOString().split('T')[0],
          invoiceNumber: oneTimeData.advanceInvoiceNumber || '',
          status: 'pending',
          notes: '',
        });
      }
      // Add final payment
      if (oneTimeData.remainingAmount) {
        schedule.push({
          id: `payment-${Date.now()}-final`,
          paymentName: 'Final Payment',
          amount: parseFloat(oneTimeData.remainingAmount),
          dueDate: oneTimeData.dueDate,
          invoiceNumber: oneTimeData.finalInvoiceNumber || '',
          status: 'pending',
          notes: '',
        });
      }
    } else {
      // Custom payment type - use custom data
      if (customData.amount && customData.dueDate) {
        schedule.push({
          id: `payment-${Date.now()}`,
          paymentName: customData.paymentName || 'Payment',
          amount: parseFloat(customData.amount),
          dueDate: customData.dueDate,
          invoiceNumber: customData.invoiceNumber || '',
          status: 'pending',
          notes: '',
        });
      }
    }

    return schedule;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.projectName || !formData.clientName) {
      toast.error('Please fill in project name and client name');
      return;
    }

    // Validate custom payment type fields
    if (!['outsourced', 'recurring', 'one-time'].includes(formData.paymentType)) {
      if (!customData.amount || !customData.dueDate) {
        toast.error('Please fill in custom payment details');
        return;
      }
    }

    const paymentSchedule = generatePaymentSchedule();

    if (paymentSchedule.length === 0) {
      toast.error('Please add at least one payment');
      return;
    }

    const projectData = {
      ...formData,
      ...(formData.paymentType === 'outsourced' ? outsourcedData : {}),
      ...(formData.paymentType === 'recurring' ? recurringData : {}),
      ...(formData.paymentType === 'one-time' ? oneTimeData : {}),
      ...(!['outsourced', 'recurring', 'one-time'].includes(formData.paymentType) ? customData : {}),
      paymentSchedule,
      createdBy: user?.id || 'anonymous',
      createdByEmail: user?.email || '',
      status: 'active',
    };

    addProject(projectData);
    toast.success('Project created successfully!');
    navigate('/projects');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      <div>
        <Button variant="ghost" className="gap-2 mb-4" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Button>
        <h1 className="text-3xl font-bold">Create New Project</h1>
        <p className="text-muted-foreground mt-1">Add a new project and set up payment schedule</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Enter project and client details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name *</Label>
                <Input
                  id="projectName"
                  placeholder="e.g., E-commerce Website"
                  value={formData.projectName}
                  onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientName">Client Name *</Label>
                <Input
                  id="clientName"
                  placeholder="e.g., ABC Corporation"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  placeholder="e.g., INV-2025-001"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentType">Payment Type *</Label>
              <Select
                value={formData.paymentType}
                onValueChange={(value) => {
                  if (value === 'add-custom') {
                    setShowAddPaymentType(true);
                  } else {
                    setFormData({ ...formData, paymentType: value });
                  }
                }}
              >
                <SelectTrigger id="paymentType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allPaymentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="add-custom" className="font-semibold text-primary">
                    <Plus className="h-4 w-4 mr-2 inline" />
                    Add Custom Type
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Payment Type Specific Fields */}
        {formData.paymentType === 'outsourced' && (
          <Card>
            <CardHeader>
              <CardTitle>Outsourced Project Details</CardTitle>
              <CardDescription>Set up milestones and payment schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="totalValue">Total Project Value</Label>
                  <Input
                    id="totalValue"
                    type="number"
                    placeholder="500000"
                    value={outsourcedData.totalValue}
                    onChange={(e) => setOutsourcedData({ ...outsourcedData, totalValue: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="advanceAmount">Advance Amount</Label>
                  <Input
                    id="advanceAmount"
                    type="number"
                    placeholder="100000"
                    value={outsourcedData.advanceAmount}
                    onChange={(e) => setOutsourcedData({ ...outsourcedData, advanceAmount: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outsourcingPartner">Outsourcing Partner</Label>
                  <Input
                    id="outsourcingPartner"
                    placeholder="SmartData"
                    value={outsourcedData.outsourcingPartner}
                    onChange={(e) => setOutsourcedData({ ...outsourcedData, outsourcingPartner: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="advanceInvoiceNumber">Advance Invoice No.</Label>
                  <Input
                    id="advanceInvoiceNumber"
                    placeholder="e.g., INV-2024-001"
                    value={outsourcedData.advanceInvoiceNumber}
                    onChange={(e) => setOutsourcedData({ ...outsourcedData, advanceInvoiceNumber: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Milestones</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddMilestone}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Milestone
                  </Button>
                </div>
                {outsourcedData.milestones.map((milestone, index) => (
                  <div key={index} className="p-3 rounded-lg border space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Milestone name"
                        value={milestone.paymentName}
                        onChange={(e) => handleMilestoneChange(index, 'paymentName', e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={milestone.amount}
                        onChange={(e) => handleMilestoneChange(index, 'amount', e.target.value)}
                      />
                      <Input
                        type="date"
                        value={milestone.dueDate}
                        onChange={(e) => handleMilestoneChange(index, 'dueDate', e.target.value)}
                      />
                      {outsourcedData.milestones.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveMilestone(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Invoice Number (e.g., INV-001)"
                      value={milestone.invoiceNumber || ''}
                      onChange={(e) => handleMilestoneChange(index, 'invoiceNumber', e.target.value)}
                      className="max-w-md"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {formData.paymentType === 'recurring' && (
          <Card>
            <CardHeader>
              <CardTitle>Recurring Payment Details</CardTitle>
              <CardDescription>Set up recurring payment schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="recTotalValue">Total Contract Value (Optional)</Label>
                  <Input
                    id="recTotalValue"
                    type="number"
                    placeholder="240000"
                    value={recurringData.totalValue}
                    onChange={(e) => setRecurringData({ ...recurringData, totalValue: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={recurringData.startDate}
                    onChange={(e) => setRecurringData({ ...recurringData, startDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="recurrence">Recurrence *</Label>
                  <Select
                    value={recurringData.recurrence}
                    onValueChange={(value) => setRecurringData({ ...recurringData, recurrence: value })}
                  >
                    <SelectTrigger id="recurrence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="half-yearly">Half-yearly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="planAmount">Plan Amount *</Label>
                  <Input
                    id="planAmount"
                    type="number"
                    placeholder="20000"
                    value={recurringData.planAmount}
                    onChange={(e) => setRecurringData({ ...recurringData, planAmount: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Payment Schedule — Invoice Numbers</Label>
                <p className="text-sm text-muted-foreground">
                  Enter the invoice number that will be raised for each recurring payment.
                </p>
                {computeRecurringPayments().length > 0 ? (
                  <div className="space-y-2">
                    {computeRecurringPayments().map((payment, index) => (
                      <div key={index} className="flex items-center gap-3 flex-wrap rounded-lg border p-3">
                        <div className="flex-1 min-w-[180px]">
                          <p className="text-sm font-medium">{payment.paymentName}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(payment.dueDate)} · {formatCurrency(payment.amount)}
                          </p>
                        </div>
                        <div className="w-full sm:w-72">
                          <Input
                            placeholder={`Invoice No. for payment ${index + 1}`}
                            value={recurringData.paymentInvoices?.[index] || ''}
                            onChange={(e) => handleRecurringInvoiceChange(index, e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Enter a start date and plan amount to preview the payment schedule and add invoice numbers.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {formData.paymentType === 'one-time' && (
          <Card>
            <CardHeader>
              <CardTitle>One-time Payment Details</CardTitle>
              <CardDescription>Set up split payment schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="totalAmount">Total Amount *</Label>
                  <Input
                    id="totalAmount"
                    type="number"
                    placeholder="150000"
                    value={oneTimeData.totalAmount}
                    onChange={(e) => {
                      const total = parseFloat(e.target.value) || 0;
                      const advance = parseFloat(oneTimeData.advanceAmount) || 0;
                      setOneTimeData({
                        ...oneTimeData,
                        totalAmount: e.target.value,
                        remainingAmount: (total - advance).toString(),
                      });
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oneAdvance">Advance Amount</Label>
                  <Input
                    id="oneAdvance"
                    type="number"
                    placeholder="60000"
                    value={oneTimeData.advanceAmount}
                    onChange={(e) => {
                      const total = parseFloat(oneTimeData.totalAmount) || 0;
                      const advance = parseFloat(e.target.value) || 0;
                      setOneTimeData({
                        ...oneTimeData,
                        advanceAmount: e.target.value,
                        remainingAmount: (total - advance).toString(),
                      });
                    }}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="remaining">Remaining Amount</Label>
                  <Input
                    id="remaining"
                    type="number"
                    placeholder="90000"
                    value={oneTimeData.remainingAmount}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oneDueDate">Final Payment Due Date *</Label>
                  <Input
                    id="oneDueDate"
                    type="date"
                    value={oneTimeData.dueDate}
                    onChange={(e) => setOneTimeData({ ...oneTimeData, dueDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="oneAdvanceInvoiceNumber">Advance Payment Invoice No.</Label>
                  <Input
                    id="oneAdvanceInvoiceNumber"
                    placeholder="e.g., INV-101"
                    value={oneTimeData.advanceInvoiceNumber}
                    onChange={(e) => setOneTimeData({ ...oneTimeData, advanceInvoiceNumber: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="finalInvoiceNumber">Final Payment Invoice No.</Label>
                  <Input
                    id="finalInvoiceNumber"
                    placeholder="e.g., INV-102"
                    value={oneTimeData.finalInvoiceNumber}
                    onChange={(e) => setOneTimeData({ ...oneTimeData, finalInvoiceNumber: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Payment Type Fields */}
        {!['outsourced', 'recurring', 'one-time'].includes(formData.paymentType) && (
          <Card>
            <CardHeader>
              <CardTitle>Custom Payment Details</CardTitle>
              <CardDescription>Set up your custom payment schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="customPaymentName">Payment Name *</Label>
                  <Input
                    id="customPaymentName"
                    placeholder="e.g., Initial Payment"
                    value={customData.paymentName}
                    onChange={(e) => setCustomData({ ...customData, paymentName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customAmount">Amount *</Label>
                  <Input
                    id="customAmount"
                    type="number"
                    placeholder="e.g., 50000"
                    value={customData.amount}
                    onChange={(e) => setCustomData({ ...customData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customDueDate">Due Date *</Label>
                  <Input
                    id="customDueDate"
                    type="date"
                    value={customData.dueDate}
                    onChange={(e) => setCustomData({ ...customData, dueDate: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customInvoiceNumber">Invoice Number</Label>
                  <Input
                    id="customInvoiceNumber"
                    placeholder="e.g., INV-001"
                    value={customData.invoiceNumber}
                    onChange={(e) => setCustomData({ ...customData, invoiceNumber: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/projects')}>
            Cancel
          </Button>
          <Button type="submit" size="lg">
            Create Project
          </Button>
        </div>
      </form>
      
      {/* Add Custom Payment Type Dialog */}
      <Dialog open={showAddPaymentType} onOpenChange={setShowAddPaymentType}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Payment Type</DialogTitle>
            <DialogDescription>
              Enter a name for your custom payment type. This will be added to the payment type dropdown.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPaymentType">Payment Type Name</Label>
              <Input
                id="newPaymentType"
                placeholder="e.g., Retainer, Hourly, etc."
                value={newPaymentType}
                onChange={(e) => setNewPaymentType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPaymentType();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentType(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPaymentType}>
              Add Payment Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};